import { Component, OnInit, AfterViewInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgxSmartModalService } from '@bssh/comp-lib';
import { ITransferModalInput, ITransferModalOutput } from '../model/action-modal';
import { DomainOption } from '../model/domain-option';
import { ModalTexts } from '../modal-texts';
import { BaseModalComponent } from '../base-modal/base-modal.component';
import { ResourceType } from '@app/core/model/resource-type';


export enum TransferMode {
  ANOTHER_USER = 'anotherUser',
  MYSELF = 'myself'
}

interface TransferModeOption {
  value: TransferMode;
  text: string;
}

@Component({
  selector: 'app-transfer-modal',
  templateUrl: './transfer-modal.component.html',
  styleUrls: ['./transfer-modal.component.scss'],
  exportAs: 'appTransferModal',
})
export class TransferModalComponent extends BaseModalComponent implements AfterViewInit, OnInit {
  resourceName: string;
  resourceType: ResourceType;
  public alertMessage = '';
  public confirmButtonText = ModalTexts.TRANSFER_OWNERSHIP.CONFIRM_BUTTON_TEXT;
  public modalTitle = ModalTexts.TRANSFER_OWNERSHIP.MODAL_TITLE;
  public inputLabel = ModalTexts.TRANSFER_OWNERSHIP.INPUT_LABEL;
  public inputPlaceholder = ModalTexts.EMAIL;
  public optionalMessageLabel = ModalTexts.OPTIONAL_MESSAGE_LABEL;
  public optionalMessagePlaceholder = ModalTexts.OPTIONAL_MESSAGE_PLACEHOLDER;
  public canTransfer = true;
  public modalType = 'TransferModal';

  public TransferMode = TransferMode;

  public transferModeOptions: TransferModeOption[] = [
    { value: TransferMode.ANOTHER_USER, text: 'Another user in current domain' },
    { value: TransferMode.MYSELF, text: 'Myself in enterprise domain' },
  ];
  public transferModeControl = new FormControl(TransferMode.ANOTHER_USER);

  public domainOptions: DomainOption[] = [];
  public domainControl = new FormControl('domainControl');
  public optionalMessageControl = new FormControl('');
  public showTransferModeSection = false;

  private loggedInUserEmail: string = null;

  constructor(public ngxSmartModalService: NgxSmartModalService) {
    super(ngxSmartModalService);
  }

  get transferMode(): TransferMode {
    return this.transferModeControl.value;
  }

  ngOnInit() {
    super.ngOnInit();
    this.disableConfirmButton = true;

    this.subs.sink = this.transferModeControl.valueChanges.subscribe((mode: TransferMode) => {
      this.domainControl.setValue(null);
    });

    this.subs.sink = this.domainControl.valueChanges.subscribe((selectedDomainId: string) => {
      if (this.transferMode === TransferMode.MYSELF) {
        this.disableConfirmButton = !this.canTransfer || !selectedDomainId;
        const _data: ITransferModalOutput = {
          email: this.loggedInUserEmail,
          targetDomainId: selectedDomainId,
          optionalMessage: this.optionalMessageControl.value
        };
        this.modal.setData(_data, true);
      }
    });

    this.subs.sink = this.data.subscribe((response: ITransferModalInput) => {
      if (response) {
        this.resourceName = response.resourceName;
        this.resourceType = response.resourceType;
        this.domainOptions = response.availableDomains || [];
        this.showTransferModeSection = !!response.isPublicDomainUser;
        this.loggedInUserEmail = response.loggedInUserEmail || null;

        // check if there is any error input to modal
        if (response.errorMsg) {
          this.error.next(response.errorMsg);
          this.canTransfer = false;
          return;
        }

        // else display alert message
        this.alertMessage = response.alertMsg;
      }
    });
  }

  dataChanged($event): void {
    // not setting the modal data, if transfer cannot be done
    // confirm button remains hidden
    if (!this.canTransfer) {
      return;
    }

    this.optionalMessageControl.setValue($event.optionalMessage);
    if (this.transferMode === TransferMode.MYSELF) {
      // for cross domain transfer, email input is not required as it will be transferred to current user email
      // so set the modal data directly with current user email and selected target domain id
      const _data: ITransferModalOutput = {
        email: this.loggedInUserEmail,
        targetDomainId: this.domainControl.value,
        optionalMessage: $event.optionalMessage
      };
      this.modal.setData(_data, true);
      this.disableConfirmButton = !this.canTransfer || !_data.targetDomainId;
    } else {
      // else set the incoming input values to modal data
      const _data: ITransferModalOutput = {
        email: $event.inputValue,
        optionalMessage: $event.optionalMessage
      };
      this.modal.setData(_data, true);

      this.disableConfirmButton = !($event.inputValue && $event.isInputValid);
    }
  }

  protected constructError(error: any): string {
    if (error.name === 'TimeoutError') {
      let singularResourceType;

      switch (this.resourceType) {
        case ResourceType.PROJECT:
        case ResourceType.PROJECTS:
          singularResourceType = ResourceType.PROJECT;
          break;
        case ResourceType.RUN:
        case ResourceType.RUNS:
          singularResourceType = ResourceType.RUN;
          break;
      }

      // This is handling special case for when size of resource is too large for transfer and API times out
      return ModalTexts.TRANSFER_OWNERSHIP.SEND.RESOURCE_SIZE_ERROR(singularResourceType);
    } else {
      return error.error.ResponseStatus.Message;
    }
  }
}
